import './widget.css';

import { IonTextarea } from '@ionic/react';
import { FieldConfig, useField } from 'formik';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IonItem } from '../../../../../components/ionic/IonItem';
import {
  forwardTouchEndEventToInput,
  forwardTouchStartEventToInput,
} from '../../../../../util/input';
import { useWidgetClasses } from '../../hook/widget.hooks';
import { WidgetLabel } from './widget-label';
import {
  TextWidgetProps,
  WidgetRef,
  createWidgetExtension,
} from './widget.types';

interface TextInputProps extends TextWidgetProps {
  ionTextareaProps?: React.ComponentProps<typeof IonTextarea>;
}

const TextInput = (props: TextInputProps & FieldConfig): ReactElement => {
  const {
    label,
    description,
    showDescription,
    disabled,
    ionItemProps,
    extensions,
    placeholder,
    selectOnFocus,
    subtitle,
    subtitleColor,
    ionTextareaProps,
    isNested,
    isPaywalled,
    transparentBackground,
    ...otherProps
  } = props;

  const { className: ionItemClassName, ...restIonItemProps } =
    ionItemProps ?? {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);
  const inputRef = useRef<HTMLIonTextareaElement>(null);
  const [isDisabled, setIsDisabled] = useState(disabled ?? false);

  const rightElement = useMemo(() => {
    return createWidgetExtension(extensions?.rightElement, {
      field: [field, meta, helper],
      widgetRef: inputRef.current as WidgetRef,
      setDisabled: setIsDisabled,
      isDisabled,
    });
  }, [extensions?.rightElement, field, helper, isDisabled, meta]);

  useEffect(() => {
    if (disabled !== undefined) {
      setIsDisabled(disabled);
    }
  }, [disabled]);

  const classList = useWidgetClasses({
    isNested,
    transparentBackground,
    isPaywalled,
  });

  return (
    <IonItem
      className={`${classList} ${ionItemClassName || ''}`.trim()}
      lines="full"
      disabled={isDisabled}
      {...restIonItemProps}
      onClick={async () => {
        if (selectOnFocus) {
          const nativeInput = await inputRef.current?.getInputElement();
          nativeInput?.select();
        }
      }}
      onTouchStart={(e) => {
        forwardTouchStartEventToInput(e, inputRef.current);
      }}
      onTouchEnd={(e) => {
        forwardTouchEndEventToInput(e, inputRef.current);
      }}
    >
      <IonTextarea
        aria-label={label ?? otherProps.name}
        labelPlacement={label ? 'floating' : undefined}
        data-cy={otherProps.name}
        ref={inputRef}
        disabled={isDisabled}
        value={field.value}
        debounce={250}
        onIonInput={(e) => {
          if (isPaywalled) {
            return;
          }

          helper.setValue(e.detail.value);
        }}
        enterkeyhint={'done'}
        placeholder={placeholder}
        {...ionTextareaProps}
      >
        <div slot={'label'}>
          <WidgetLabel
            label={label}
            description={description}
            error={meta.error}
            forceDisable={isDisabled}
            subtitle={subtitle}
            subtitleColor={subtitleColor}
          />
        </div>
      </IonTextarea>
      {rightElement}
    </IonItem>
  );
};

export { TextInput };
